import { SparkIcon } from '../SparkIcon';
import { TruncatedText } from '../Text/TruncatedText';
import { DropdownCheckIcon } from './DropdownCheckIcon';
import './DropdownDescriptiveMenu.css';

export type DropdownDescriptiveItemState = 'default' | 'hover' | 'selected' | 'disabled';

/** Two-line dropdown item with a title and a description subtitle. */
export type DropdownDescriptiveItemProps = {
  /** Primary option text displayed prominently. */
  title?: string;
  /** Secondary description displayed below the title. */
  description?: string;
  state?: DropdownDescriptiveItemState;
  /** Storybook-only visual state override for static matrices. */
  visualState?: DropdownDescriptiveItemState;
  showPrefixIcon?: boolean;
  /** Icon slug for the prefix icon (from the Spark icon set). Defaults to "user". */
  prefixIconSlug?: string;
  /** DOM id for a11y (`aria-activedescendant`). */
  id?: string;
  className?: string;
  onClick?: () => void;
};

export function DropdownDescriptiveItem({
  title = 'Option title',
  description = 'Option description text',
  state = 'default',
  visualState,
  showPrefixIcon = false,
  prefixIconSlug = 'user',
  id,
  className = '',
  onClick,
}: DropdownDescriptiveItemProps) {
  const resolvedState = visualState ?? state;
  const isDisabled = resolvedState === 'disabled';
  const isSelected = resolvedState === 'selected';
  const isHover = resolvedState === 'hover';

  const rootClasses = [
    'spark-dropdown-descriptive-item',
    isHover ? 'spark-dropdown-descriptive-item-hover' : '',
    isSelected ? 'spark-dropdown-descriptive-item-selected' : '',
    isDisabled ? 'spark-dropdown-descriptive-item-disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      id={id}
      className={rootClasses}
      role="option"
      aria-selected={isSelected || undefined}
      aria-disabled={isDisabled || undefined}
      onClick={isDisabled ? undefined : onClick}
    >
      <div className="spark-dropdown-descriptive-item-text">
        <div className="spark-dropdown-descriptive-item-title-row">
          {showPrefixIcon ? (
            <SparkIcon slug={prefixIconSlug} size="small" className="spark-dropdown-descriptive-item-prefix" />
          ) : null}
          {/* Tooltip with the full text only when the line is actually clipped. */}
          <TruncatedText text={title} maxWidth="100%" className="spark-dropdown-descriptive-item-title" />
        </div>
        <TruncatedText
          text={description}
          maxWidth="100%"
          className="spark-dropdown-descriptive-item-description"
        />
      </div>
      {isSelected ? <DropdownCheckIcon className="spark-dropdown-descriptive-item-check" /> : null}
    </div>
  );
}
